import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import './styles.css';
import NumberFormat from 'react-number-format';
import CriteriaList from '../../../../components/CriteriaList';
import ENDPOINT from '../../../../constants/endpoints';
import ERROR_CODE from '../../../../constants/error-codes';
import ERROR_FIELD from '../../../../constants/error-fields';
import ROUTE from '../../../../constants/route';
import useApi from '../../../../hooks/useApi';
import BasicDialog from '../../../Dialog/components/BasicDialog';
import ResponseErrorDialog from '../../../Dialog/components/ResponseErrorDialog';
import SendInboundsForm from '../../../Form/components/SendInboundsForm';
import InBoundsHistory from '../InBoundsHistory';

const InBounds = props => {
  const { inboundsCount, onInboundsUpdate } = props;

  const history = useRef(null);

  const api = useApi();

  const handleSendForm = (values, { setErrors, setSubmitting, resetForm }) => {
    api.post(
      ENDPOINT.ACTION.INBOUNDS_SEND,
      {
        CompanyID: values.CompanyID.value,
        Message: values.Message,
      },
      {
        handleError: response => {
          if (
            response.data.code &&
            ERROR_CODE.CRITERIA_MISMATCH === response.data.code &&
            response.data.filters
          ) {
            BasicDialog({
              bodyRender: (
                <div>
                  You are unable to send inbound requests for this company
                  because the firm has specified the below criteria{' '}
                  <CriteriaList records={response.data.filters} />
                </div>
              ),
              popupStyles: { maxWidth: '850px' },
              buttonStyles: { width: 150 },
            });
          } else if (response.data.fields && typeof setErrors === 'function') {
            const obj = {};
            for (let [key, value] of Object.entries(response.data.fields)) {
              const error = isArray(value) ? value[0] : value;
              obj[key] = ERROR_FIELD[error] || `${error}`;
            }
            setErrors(obj);
          } else {
            ResponseErrorDialog({ response });
          }
        },
        handleSuccess: ({ inboundsLeft }) => {
          resetForm();
          handleInboundsUpdate(inboundsLeft);
        },
        handleComplete: () => setSubmitting(false),
      }
    );
  };

  const handleInboundsUpdate = count => {
    onInboundsUpdate(count);
    history.current.refresh();
  };

  const handleClickLearnMore = () => {
    BasicDialog({
      bodyRender: (
        <p style={{ maxWidth: '500px' }}>
          An InBound sends your profile and message to a hiring manager at the
          selected firm. If that hiring manager responds within 30 days, your
          InBound is credited back to your account. As it is often not clear
          which investment professional is acting as a hiring manager in a given
          year, InBounds are a seamless way to get your name in front of
          relevant professionals. Hiring managers can also set requirements for
          InBounds that limit the types of candidates that can apply. All
          candidates are forced to be selective when sending InBounds as they
          must be purchased. This payment is analogous to an application fee as
          it deters candidates from InBounding every firm and allows hiring
          managers to see an InBound as a strong indication of interest.
        </p>
      ),
    });
  };

  return (
    <>
      <div className="sidebar__widget inbounds-widget">
        <div className="inbounds-widget__block">
          <h4 className="inbounds-widget__title">Send InBounds</h4>
          <span
            className="inbounds-widget__link"
            onClick={handleClickLearnMore}
          >
            Learn More
          </span>
        </div>

        <div className="inbounds-widget__block">
          <h4 className="inbounds-widget__title">
            <NumberFormat
              thousandSeparator
              value={inboundsCount}
              displayType={'text'}
            />{' '}
            InBounds to use.
          </h4>
          <p className="inbounds-widget__disclaimer">Need more InBounds?</p>
          <Link
            className="inbounds-widget__link"
            to={{
              pathname: ROUTE.CHECKOUT_INBOUNDS,
              state: { returnUrl: window.location.pathname },
            }}
          >
            get more here
          </Link>
        </div>

        <SendInboundsForm onSaveForm={handleSendForm} />
      </div>

      <InBoundsHistory ref={history} isCandidate />
    </>
  );
};

InBounds.propTypes = {
  onInboundsUpdate: PropTypes.func.isRequired,
  inboundsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

export default InBounds;
